import _ from 'lodash';
import {
  all,
  call,
  put,
  takeLatest,
  takeEvery,
  select,
} from 'redux-saga/effects';
import { APIResponse } from 'spw-redux/types';
import * as constants from 'spw-constants';
import { camelize, replaceAtIndex, textOrDash } from 'spw-shared/utils';
import { omit, pipe } from 'ramda';
import { handleError } from 'spw-redux/modules/UI/messages/actions';
import { WorksheetDefaultCategory } from 'spw-redux/modules/worksheet/types';
import { currentProject } from 'spw-redux/modules/projects/selectors';
import { IProject } from 'spw-redux/modules/projects/types';
import { setCurrentProject } from 'spw-redux/modules/projects/actions';
import {
  actions,
  GET_KEY_DATA,
  GET_USF_TOTAL,
  GET_RSF_TOTAL,
  GET_WORKSPACE_BREAKDOWN,
  GET_SPACES_BREAKDOWN,
  GET_SEATS_OCCUPIED,
  GET_WORKPLACE_BEHAVIOR,
  UPDATE_SPACES_BY_TYPE,
  GET_ALTERNATIVE_WORKPOINTS_BREAKDOWN,
  getAltWorkpointsBreakdownSucceeded,
  UPDATE_PROGRAM_SUMMARY_CARD_ICON,
} from './actions';
import { ISpaceBreakDown, ISeatsOccupied } from './types';
import {
  getWorkspaceBreakdown as getWorkspaceBreakdownApi,
  getUsfTotal as getUsfTotalApi,
  getRsfTotal as getRsfTotalApi,
  getSpacesBreakdown as getSpacesBreakdownApi,
  getKeyData as getKeyDataApi,
  getSeatsOccupied as getSeatsOccupiedApi,
  getWorkplaceBehavior as getWorkspaceBehaviorApi,
  updateSpacesByType as updateSpacesByTypeApi,
  getAltWorkpointsBreakdownApi,
  updateProgramSummaryCardIconApi,
} from '../../../api/esa-program-summary';
import { fetchFloorplanShapes } from '../floorplan/actions';

function* getKeyData(action: ReturnType<typeof actions.getKeyData>) {
  try {
    const keyDataResponse: APIResponse<typeof getKeyDataApi> = yield call(
      getKeyDataApi,
      action.payload
    );

    const { keyData } = keyDataResponse.data;
    yield put(actions.getKeyDataSucceeded(keyData));
  } catch (error) {
    yield put(handleError(error));
  }
}

function* getUsfTotal(action: ReturnType<typeof actions.getUsfTotal>) {
  try {
    const response: APIResponse<typeof getUsfTotalApi> = yield call(
      getUsfTotalApi,
      action.payload
    );
    const { usfTotal } = response.data;
    usfTotal.total = pipe(Math.round, textOrDash)(usfTotal.total);
    usfTotal.subtotal = pipe(Math.round, textOrDash)(usfTotal.subtotal);
    yield put(actions.getUsfTotalSucceeded(usfTotal));
  } catch (error) {
    yield put(handleError(error));
  }
}

function* getRsfTotal(action: ReturnType<typeof actions.getRsfTotal>) {
  try {
    const response: APIResponse<typeof getRsfTotalApi> = yield call(
      getRsfTotalApi,
      action.payload
    );
    const { rsfTotal } = response.data;
    rsfTotal.totalRsf = pipe(Math.round, textOrDash)(rsfTotal.totalRsf);
    rsfTotal.buildingRsfLoadFactor = textOrDash(rsfTotal.buildingRsfLoadFactor);
    yield put(actions.getRsfTotalSucceeded(rsfTotal));
  } catch (error) {
    yield put(handleError(error));
  }
}

function* getWorkspaceBreakdown(
  action: ReturnType<typeof actions.getWorkspaceBreakdown>
) {
  try {
    const response: APIResponse<typeof getWorkspaceBreakdownApi> = yield call(
      getWorkspaceBreakdownApi,
      action.payload
    );
    const { workspaceBreakdown } = response.data;
    yield put(actions.getWorkspaceBreakdownSucceeded(workspaceBreakdown));
  } catch (error) {
    yield put(handleError(error));
  }
}

function* getSeatsOccupied(
  action: ReturnType<typeof actions.getSeatsOccupied>
) {
  const seatsOccupied: ISeatsOccupied = {
    individualWorkspaces: [],
    meetingSpaces: [],
    amenitySpaces: [],
    supportSpaces: [],
  };
  const responses: APIResponse<typeof getSeatsOccupiedApi>[] = yield all(
    [
      constants.INDIVIDUAL_SPACES_CATEGORY,
      constants.MEETING_SPACES_CATEGORY,
      constants.AMENITY_SPACES_CATEGORY,
      constants.SUPPORT_SPACES_CATEGORY,
    ].map((category) => {
      return call(getSeatsOccupiedApi, action.payload, category);
    })
  );
  const [
    individualWorkspaces,
    meetingSpaces,
    amenitySpaces,
    supportSpaces,
  ] = responses;

  seatsOccupied.individualWorkspaces = individualWorkspaces.data.seatsOccupied;
  seatsOccupied.meetingSpaces = meetingSpaces.data.seatsOccupied;
  seatsOccupied.amenitySpaces = amenitySpaces.data.seatsOccupied;
  seatsOccupied.supportSpaces = supportSpaces.data.seatsOccupied;
  yield put(actions.getSeatsOccupiedSucceeded(camelize(seatsOccupied)));
}

function* getWorkplaceBehavior(
  action: ReturnType<typeof actions.getWorkplaceBehavior>
) {
  try {
    const response: APIResponse<typeof getWorkspaceBehaviorApi> = yield call(
      getWorkspaceBehaviorApi,
      action.payload
    );
    const { workplaceBehavior } = response.data;
    yield put(actions.getWorkplaceBehaviorSucceeded(workplaceBehavior));
  } catch (error) {
    yield put(handleError(error));
  }
}

function* updateSpacesByType(
  action: ReturnType<typeof actions.updateSpacesByType>
) {
  try {
    yield call(updateSpacesByTypeApi, omit(['callback'], action.payload));
    yield put(fetchFloorplanShapes());
    action.payload.callback();
  } catch (error) {
    yield put(handleError(error));
  }
}

function* getSpacesBreakDown(
  action: ReturnType<typeof actions.getSpacesBreakDown>
) {
  const responses: APIResponse<typeof getSpacesBreakdownApi>[] = yield all(
    [
      constants.INDIVIDUAL_SPACES_CATEGORY,
      constants.MEETING_SPACES_CATEGORY,
      constants.AMENITY_SPACES_CATEGORY,
      constants.SUPPORT_SPACES_CATEGORY,
    ].map((category) => {
      return call(getSpacesBreakdownApi, action.payload, category);
    })
  );

  const [
    individualWorkspaces,
    meetingSpaces,
    amenitySpaces,
    supportSpaces,
  ] = responses.map(camelize);
  const formatBreakdown = (
    space: ISpaceBreakDown,
    category: WorksheetDefaultCategory
  ) => {
    return {
      ...space,
      usf: Math.round(space.usf),
      usfAvg: Math.round(space.usfAvg),
      category,
    };
  };

  const orderedCats = [
    constants.INDIVIDUAL_SPACES_CATEGORY,
    constants.MEETING_SPACES_CATEGORY,
    constants.AMENITY_SPACES_CATEGORY,
    constants.SUPPORT_SPACES_CATEGORY,
  ];

  const spacesList = _([
    individualWorkspaces,
    meetingSpaces,
    amenitySpaces,
    supportSpaces,
  ])
    .map((cat, i) => [
      ...cat.data.spacesBreakdown.map((space: ISpaceBreakDown) => ({
        ...space,
        category: orderedCats[i],
      })),
    ])
    .flatten()
    .map((space: ISpaceBreakDown) => {
      let { category }: { category: WorksheetDefaultCategory } = space;
      if (space.category === constants.INDIVIDUAL_SPACES_CATEGORY) {
        category = space.spaceType.includes('office')
          ? constants.PRIVATE_OFFICES_SUBCATEGORY
          : constants.INDIVIDUAL_SPACES_CATEGORY;
      }
      return formatBreakdown(space, category);
    })
    .value();

  const spacesBreakdown = _.groupBy(
    spacesList,
    ({ category }: { category: WorksheetDefaultCategory }) =>
      category === constants.INDIVIDUAL_SPACES_CATEGORY ||
      category === constants.PRIVATE_OFFICES_SUBCATEGORY
        ? constants.INDIVIDUAL_SPACES_CATEGORY
        : category
  );

  for (const v in orderedCats) {
    if (!spacesBreakdown[orderedCats[v]]) {
      spacesBreakdown[orderedCats[v]] = [];
    }
  }

  yield put(actions.getSpacesBreakdownSucceeded(camelize(spacesBreakdown)));
}

function* getAltWorkpointsBreakdownCall(
  action: ReturnType<typeof actions.getAltWorkpointsBreakdown>
) {
  try {
    const response: APIResponse<typeof getAltWorkpointsBreakdownApi> = yield call(
      getAltWorkpointsBreakdownApi,
      action.payload
    );
    yield put(
      getAltWorkpointsBreakdownSucceeded(
        response.data.alternativeWorkpointsBreakdown
      )
    );
  } catch (error) {
    yield put(handleError(error));
  }
}

function* updateProgramSummaryCardIconCall(
  action: ReturnType<typeof actions.updateProgramSummaryCardIcon>
) {
  try {
    const { cardIcon } = action.payload;
    const project: IProject = yield select(currentProject);

    const response: APIResponse<typeof updateProgramSummaryCardIconApi> = yield call(
      updateProgramSummaryCardIconApi,
      project.id,
      cardIcon
    );

    const { cardIcon: updatedCardIcon } = response.data;

    const cardIconIndex = project.programSummaryCardIcons.findIndex(
      (programSummaryCardIcon) =>
        programSummaryCardIcon.cardType === updatedCardIcon.cardType
    );

    const updatedProgramSummaryCardIcons = replaceAtIndex(
      [...project.programSummaryCardIcons],
      updatedCardIcon,
      cardIconIndex
    );

    yield put(
      setCurrentProject({
        ...project,
        programSummaryCardIcons: updatedProgramSummaryCardIcons,
      })
    );
  } catch (error) {
    yield put(handleError(error));
  }
}

function* saga() {
  yield all([
    takeLatest(GET_KEY_DATA, getKeyData),
    takeLatest(GET_USF_TOTAL, getUsfTotal),
    takeLatest(GET_RSF_TOTAL, getRsfTotal),
    takeEvery(GET_WORKSPACE_BREAKDOWN, getWorkspaceBreakdown),
    takeLatest(GET_SPACES_BREAKDOWN, getSpacesBreakDown),
    takeLatest(
      GET_ALTERNATIVE_WORKPOINTS_BREAKDOWN,
      getAltWorkpointsBreakdownCall
    ),

    takeLatest(GET_SEATS_OCCUPIED, getSeatsOccupied),
    takeLatest(GET_WORKPLACE_BEHAVIOR, getWorkplaceBehavior),
    takeLatest(UPDATE_SPACES_BY_TYPE, updateSpacesByType),
    takeLatest(
      UPDATE_PROGRAM_SUMMARY_CARD_ICON,
      updateProgramSummaryCardIconCall
    ),
  ]);
}

export default saga;
